import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Download, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * Self-update prompt for the sideloaded Android app (no Play Store, so no
 * built-in auto-update). Only ever renders inside the native Android shell
 * -- a no-op everywhere else (regular browser, iOS, etc.) since Capacitor.
 * isNativePlatform() is false there.
 *
 * Flow: read our own installed versionCode via @capacitor/app, compare
 * against GET /api/app/android-version's latest_version_code. If newer,
 * show a dismissible banner; tapping it opens the APK download URL in the
 * system browser (Browser.open, not the WebView) so Android's own download
 * manager + package installer handle the rest -- exactly how a sideloaded
 * app is meant to update itself.
 */
export default function AndroidUpdateBanner() {
  const [info, setInfo] = useState(null); // { latest_version_name, apk_url, release_notes }
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") return;
    let cancelled = false;
    (async () => {
      try {
        const [{ build }, { data }] = await Promise.all([
          App.getInfo(),
          api.get("/app/android-version"),
        ]);
        const installed = parseInt(build, 10) || 0;
        if (!cancelled && data?.latest_version_code > installed) {
          setInfo(data);
        }
      } catch {
        // fail-soft -- never block the app over an update-check hiccup
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!info || dismissed) return null;

  const openDownload = () => {
    Browser.open({ url: info.apk_url }).catch(() => {
      window.open(info.apk_url, "_system");
    });
  };

  return (
    <div
      className="bg-[#F7941D] text-white px-3 py-2 flex items-center gap-2.5 text-xs sm:text-sm"
      data-testid="android-update-banner"
    >
      <Download className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-bold">Update available</span>
        <span className="opacity-90"> — v{info.latest_version_name} is ready to install.</span>
      </div>
      <button
        onClick={openDownload}
        data-testid="android-update-download-btn"
        className="bg-white text-[#F7941D] font-bold px-3 py-1 rounded-lg flex-shrink-0"
      >
        Update
      </button>
      <button onClick={() => setDismissed(true)} data-testid="android-update-dismiss-btn" className="flex-shrink-0 opacity-80 hover:opacity-100">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
